import axios from "axios";
import { API_BASE_URL } from "../../../environment";
import { getAuthHeaders } from "../../../utils/authUtils";

export interface Task {
  task_id: number;
  title: string;
  category: string;
  start_date: string | null;
  due_date: string | null;
  priority: string | null;
  status: string | null;
  description: string;
  created_datetime: string | null;
  related_entity_name: string;
  related_entity_id: number;
  related_entity_title?: string;
  responsible_persons: {
    user_id: number;
    full_name: string;
    avatar_url: string;
  } | null;
  responsible_team: {
    team_id: number;
    name: string;
  } | null;
  created_by: {
    user_id: number;
    full_name: string;
    avatar_url: string;
  } | null;
  tenant_id: number;
  tenant: {
    tenant_id: number;
    tenant_name: string;
  };
}

export interface TasksResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Task[];
}

export interface TaskListParams {
  tenantId: number | string;
  page: number;
  pageSize: number;
  salesUserId?: number | string;
  searchTerm?: string;
  status?: string;
  priority?: string;
  dueDateGte?: string;
  dueDateLte?: string;
}

export const getCurrentSalesUserId = (): number | undefined => {
  const authResponse = localStorage.getItem("authResponse");
  if (!authResponse) return undefined;
  try {
    const auth = JSON.parse(authResponse);
    return auth.user_id;
  } catch (e) {
    console.warn("Could not parse auth response for user_id");
    return undefined;
  }
};

export const buildTaskListEndpoint = (params: TaskListParams): string => {
  let endpoint = `${API_BASE_URL}/task/?tenant_id=${params.tenantId}&page=${params.page}&page_size=${params.pageSize}`;

  if (params.salesUserId) {
    endpoint += `&sales_user_id=${params.salesUserId}`;
  }
  if (params.searchTerm) {
    endpoint += `&title=${encodeURIComponent(params.searchTerm)}`;
  }
  if (params.status && params.status !== "all") {
    endpoint += `&status=${encodeURIComponent(params.status)}`;
  }
  if (params.priority && params.priority !== "all") {
    endpoint += `&priority=${encodeURIComponent(params.priority)}`;
  }
  if (params.dueDateGte) {
    endpoint += `&due_date_gte=${encodeURIComponent(params.dueDateGte)}`;
  }
  if (params.dueDateLte) {
    endpoint += `&due_date_lte=${encodeURIComponent(params.dueDateLte)}`;
  }
  return endpoint;
};

export const fetchTaskList = async (
  params: TaskListParams
): Promise<TasksResponse> => {
  const endpoint = buildTaskListEndpoint(params);
  const response = await axios.get<TasksResponse>(endpoint, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const fetchTaskDetail = async (taskId: number) => {
  const response = await axios.get(`${API_BASE_URL}/task/${taskId}/`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const deleteTask = async (taskId: number) => {
  await axios.delete(`${API_BASE_URL}/task/${taskId}/`, {
    headers: getAuthHeaders(),
  });
};
